import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Divider,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { ExclamationIcon } from '@heroicons/react/outline';
import { useRef, useState } from 'react';
import { useTranslations } from 'use-intl';

import {
  SubscriptionsFilterInput,
  useCountRemindableSubscriptionInvitationsQuery,
  useRemindSubscriptionInvitationsMutation,
} from '@blockpulse3/graphql/hooks';
import {
  ResponsiveModal,
  ResponsiveModalFooter,
  ResponsiveModalProps,
  useErrorToast,
  useSuccessToast,
} from '@blockpulse3/ui/commons';

type Props = {
  fundraisingId: string;
  filters: SubscriptionsFilterInput[];
  totalCount: number;
  onClose: () => void;
} & Omit<ResponsiveModalProps, 'children' | 'onClose'>;

export function FundraisingSubscribersRemindersModal({
  fundraisingId,
  filters,
  totalCount,
  onClose,
  ...props
}: Props): JSX.Element {
  const i18nFundraisingSubscribersRemindersModal = useTranslations(
    'FundraisingSubscribersRemindersModal',
  );
  const i18nFundraisingSubscribersTable = useTranslations('FundraisingSubscribersTable');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const [isLoading, setIsLoading] = useState(false);
  const [remindSubscriptionInvitations] = useRemindSubscriptionInvitationsMutation();
  const { data } = useCountRemindableSubscriptionInvitationsQuery({
    variables: { fundraisingId, filterBy: filters },
    fetchPolicy: 'no-cache',
  });

  const remindableCount = data?.countRemindableSubscriptionInvitations || 0;
  const nonRemindableCount = totalCount - remindableCount;
  const canSendReminder = totalCount !== nonRemindableCount;

  const handleSubmit = (): void => {
    setIsLoading(true);
    const message = inputRef.current?.value;
    remindSubscriptionInvitations({
      variables: { fundraisingId, filterBy: filters, customMessage: message },
      onCompleted: (data) => {
        setIsLoading(false);
        successToast({
          title: i18nFundraisingSubscribersTable('Toast.Success', {
            nb: data.remindSubscriptionInvitations.successCount,
          }),
        });
      },
      onError: () => {
        setIsLoading(false);
        errorToast({ title: i18nFundraisingSubscribersTable('Toast.Error') });
      },
    });
    onClose();
  };

  return (
    <ResponsiveModal onClose={onClose} {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {remindableCount > 0
            ? i18nFundraisingSubscribersRemindersModal('RemindSubscribersTitle', {
                nb: remindableCount,
              })
            : i18nFundraisingSubscribersRemindersModal('RemindNoSubscribersTitle')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Alert status="warning">
            <AlertIcon as={ExclamationIcon} />
            <Stack spacing="0">
              <AlertTitle>
                {!canSendReminder
                  ? i18nFundraisingSubscribersRemindersModal('RemindSubscribersDisabled')
                  : i18nFundraisingSubscribersRemindersModal('RemindSubscribersDescription', {
                      nb: remindableCount,
                    })}
              </AlertTitle>
              {remindableCount > 0 && remindableCount < totalCount && (
                <AlertDescription>
                  {i18nFundraisingSubscribersRemindersModal('RemindSubscribersWarning', {
                    nb: nonRemindableCount,
                  })}
                </AlertDescription>
              )}
            </Stack>
          </Alert>
          {canSendReminder && (
            <Accordion allowToggle mt="2" variant="unstyled">
              <AccordionItem>
                <AccordionButton data-cy="expand-parameters">
                  <Text color="gray" fontSize="sm" textAlign="left">
                    {i18nFundraisingSubscribersRemindersModal('RemindSubscribersMessage')}
                  </Text>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel p="0">
                  <Textarea minH="200" ref={inputRef} size="sm" width="full" />
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          )}
        </ModalBody>
        <Divider />
        <ResponsiveModalFooter>
          <Button variant="secondary" onClick={onClose}>
            {i18nFundraisingSubscribersRemindersModal('Cancel')}
          </Button>
          <Button
            isDisabled={!canSendReminder || isLoading}
            isLoading={isLoading}
            onClick={handleSubmit}
          >
            {i18nFundraisingSubscribersRemindersModal('Send')}
          </Button>
        </ResponsiveModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
}
